import Color from 'color';
import formatDateIntl from 'date-fns/intlFormat/index';
import debug from 'debug';

import { COLOR_DARK, COLOR_LIGHT, TRACKING_KEY, TRACKING_PROPERTIES_KEY } from './global/constants';
import getQueryParameter from './global/helper/get-query-parameter';

import type { Language } from '$lib/types/Language';
import type { Theme } from '$lib/types/Theme';
import type { TrackingProperty } from '$lib/types/TrackingProperty';
import type { Component } from './types/Components/SharedTypes';
import type { JSONContent } from '@tiptap/core';

declare global {
    interface String {
        splice(start: number, length: number, replacement: string): string;
    }
}

const debugLog = debug('lib:helper');

// For personalization string replacement
String.prototype.splice = function (start: number, length: number, replacement: string) {
    return this.substr(0, start) + replacement + this.substr(start + length);
};

export const hexToRgb = (hex: string) => {
    // hex can be #123 or #112233
    const sanitizedHexString =
        hex.length === 4 ? '#' + hex[1] + hex[1] + hex[2] + hex[2] + hex[3] + hex[3] : hex;

    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(sanitizedHexString);

    if (!result) return { r: 0, g: 0, b: 0 };

    return {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
    };
};

export const rgbaToRgb = (rgba: string) => {
    const colors = rgba.match(/\d+/g);

    if (!colors) return { r: 0, g: 0, b: 0 };

    return {
        r: parseInt(colors[0], 16),
        g: parseInt(colors[1], 16),
        b: parseInt(colors[2], 16),
    };
};

export const isHexString = (color: string) => {
    return /^#([A-Fa-f0-9]{3}){1,2}$/i.test(color);
};

export const isRgbaString = (color: string) => {
    return /^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*(\d+(?:\.\d+)?))?\)$/.test(color);
};

export const isDark = (color: string) => {
    if (color === 'transparent') {
        return false;
    }

    try {
        const [r, g, b] = Color(color).rgb().array();
        const yiq = (r * 2126 + g * 7152 + b * 722) / 10000;
        return yiq < 128;
    } catch {
        return false;
    }
};

export const getColorByLightness = (refColor = '#000000') => {
    if (refColor.includes('linear-gradient')) return COLOR_LIGHT;

    if (refColor === 'transparent') return COLOR_DARK;

    return Color(refColor).lightness() <= 60 ? COLOR_LIGHT : COLOR_DARK;
};

/**
 * Get cookie preferences from local storage
 */
export const getCookiePreferences = (campaignId: string, campaignVersionId: string) => {
    const LS = window.localStorage;
    const preferences = LS.getItem(`${TRACKING_KEY}${campaignId}.${campaignVersionId}`);

    if (preferences) {
        return JSON.parse(preferences);
    }

    return {};
};

/**
 * Check if preferences are present in LS or not
 */
export const getCookiePreferencesPresent = (campaignId: string, campaignVersionId: string) => {
    const LS = window.localStorage;
    return !!LS.getItem(`${TRACKING_KEY}${campaignId}.${campaignVersionId}`);
};

/**
 * Set cookie preferences in local storage
 */
export const setCookiePreferences = (
    campaignId: string,
    campaignVersionId: string,
    preferences: { [key: string]: any },
) => {
    const LS = window.localStorage;

    return LS.setItem(
        `${TRACKING_KEY}${campaignId}.${campaignVersionId}`,
        JSON.stringify(preferences),
    );
};

/**
 * Add Http to URLs without http://, https://, tel: or mailto: prefix
 */
export const createExternalUrl = (
    url: string,
    campaignId?: string,
    trackingProperties?: TrackingProperty[],
) => {
    const { utm, custom, thirdParty } = getQueryParameter();
    let savedTrackingProperties = {};
    let defaultTrackingProperties = {};
    let allTrackedData = {};

    // makes sure all tracked data is used instead of just the ones set in the tracking properties
    // the `getQueryParameter` only gets the query parameters that are set in the current URL
    // we need to get all query parameters which were tracked on funnel load
    if (typeof window !== 'undefined') {
        allTrackedData = window.analytics?.user()?.traits();
    }

    if (campaignId) {
        savedTrackingProperties = getAllTrackingProperties(campaignId);
    }

    if (trackingProperties) {
        defaultTrackingProperties = getDefaultTrackingProperties(trackingProperties);
    }

    const processedUrl = replaceCustomVariables(url, {
        ...defaultTrackingProperties,
        ...savedTrackingProperties,
        ...thirdParty,
        ...utm,
        ...custom,
        ...allTrackedData,
    });

    if (!/(^mailto:)|(^tel:)|(^https?:\/\/)/i.test(url)) {
        return 'http://' + processedUrl;
    }

    return processedUrl;
};

const replaceCustomVariables = (
    url: string,
    customVariables: { [key: string]: string | (string | null)[] | null },
) => {
    Object.keys(customVariables).forEach((key) => {
        const value = customVariables[key];

        if (value) {
            url = url.replace(`@${key}`, value.toString());
        }
    });

    return url;
};

export const setTrackingProperty = (
    key: string,
    value: string | undefined,
    campaignId: string | undefined,
) => {
    const ST = window.sessionStorage;
    const personalization = JSON.parse(
        ST.getItem(`${TRACKING_PROPERTIES_KEY}${campaignId}`) || '{}',
    );

    const updatedPersonalization = personalization ? { ...personalization } : {};

    updatedPersonalization[key] = value;

    ST.setItem(`${TRACKING_PROPERTIES_KEY}${campaignId}`, JSON.stringify(updatedPersonalization));
};

export const getAllTrackingProperties = (campaignId: string) => {
    const ST = window.sessionStorage;
    const properties = ST.getItem(`${TRACKING_PROPERTIES_KEY}${campaignId}`);

    if (!properties) return {};

    return JSON.parse(ST.getItem(`${TRACKING_PROPERTIES_KEY}${campaignId}`) || '{}');
};

export const getDefaultTrackingProperties = (trackingProperties: TrackingProperty[]) => {
    const mappedTrackingProperties: { [fieldName: string]: string } = {};

    trackingProperties.forEach((property) => {
        mappedTrackingProperties[property.fieldName] = property.defaultValue;
    });

    return mappedTrackingProperties;
};

const replaceMentionLabel = (tipTapData: JSONContent, mentionId: string, newLabel: string) => {
    tipTapData.content?.forEach((paragraph) => {
        if (paragraph.content) {
            paragraph.content.forEach((textObject) => {
                if (textObject.type === 'mention' && textObject.attrs?.id === mentionId) {
                    textObject.attrs.label = newLabel;
                }
            });
        }
    });

    return tipTapData;
};

export const personalizeText = (
    wysiwyg: JSONContent,
    trackingProperties: TrackingProperty[],
    campaignId: string,
    language: Language = 'en',
) => {
    const defaultTrackingProperties = getDefaultTrackingProperties(trackingProperties);
    const savedTrackingProperties = getAllTrackingProperties(campaignId);

    // find saved tracking properties in the content and replace them
    trackingProperties.forEach((property) => {
        const replaceValue =
            savedTrackingProperties[property.fieldName] ??
            defaultTrackingProperties[property.fieldName];

        if (replaceValue) {
            wysiwyg = replaceMentionLabel(
                wysiwyg,
                property._id,
                // also parse date strings and make them more readable
                parseISODate(replaceValue, language),
            );
        }
    });

    return wysiwyg;
};

const parseISODate = (str: string, locale: string) => {
    if (isIsoDate(str)) {
        const date = new Date(str);

        if (!isNaN(date.valueOf())) {
            return formatDateIntl(
                date,
                { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' },
                { locale },
            );
        }
    }

    return str;
};

const isIsoDate = (str: string) => {
    if (!/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z/.test(str)) return false;

    const d = new Date(str);

    return d.toISOString() === str; // valid date
};

export const getColor = (color: string, themeColor: string) => {
    return !color || color === 'transparent' ? themeColor : color;
};

export const insertIntoArray = (arr: any[], value: any) => {
    return arr.reduce((result, element, index, array) => {
        result.push(element);

        if (index < array.length - 1) {
            result.push(value);
        }

        return result;
    }, []);
};

// This method receives all page components/blocks, maps them to their DisplayComponent import and
// returns an array of the resulting import promises
export const getBlockImportPromises = (
    components: Component[],
): Promise<typeof import('*.svelte')>[] => {
    const blockImports: Promise<typeof import('*.svelte')>[] = [];
    const componentTypes = components.map((component: Component) => component.componentType);

    // remove duplicates from componentTypes
    const uniqueComponentTypes = [...new Set(componentTypes)];

    debugLog('preloading page blocks:', uniqueComponentTypes);

    // The mapping/switch-case is necessary since you can't use string variables as import paths
    uniqueComponentTypes.forEach((componentType: string) => {
        switch (componentType) {
            case 'accordion': {
                blockImports.push(import('$lib/components/Blocks/Accordion'));
                break;
            }
            case 'button': {
                blockImports.push(import('$lib/components/Blocks/Button'));
                break;
            }
            case 'countdown': {
                blockImports.push(import('$lib/components/Blocks/Countdown'));
                break;
            }
            case 'divider': {
                blockImports.push(import('$lib/components/Blocks/Divider'));
                break;
            }
            case 'embed': {
                blockImports.push(import('$lib/components/Blocks/Embed'));
                break;
            }
            case 'gridColumn': {
                blockImports.push(import('$lib/components/Blocks/GridColumn'));
                break;
            }
            case 'gridRow': {
                blockImports.push(import('$lib/components/Blocks/GridRow'));
                break;
            }
            case 'icon': {
                blockImports.push(import('$lib/components/Blocks/Icon'));
                break;
            }
            case 'media': {
                // TODO(CK): Add proper preloading for Video as well
                blockImports.push(import('$lib/components/Blocks/Image'));
                break;
            }
            case 'list': {
                blockImports.push(import('$lib/components/Blocks/List'));
                break;
            }
            case 'logos': {
                blockImports.push(import('$lib/components/Blocks/Logos'));
                break;
            }
            case 'paymentForm': {
                blockImports.push(import('$lib/components/Blocks/PaymentForm'));
                break;
            }
            case 'questionForm': {
                blockImports.push(import('$lib/components/Blocks/FormQuestion'));
                break;
            }
            case 'questionMedia': {
                blockImports.push(import('$lib/components/Blocks/MediaQuestion'));
                break;
            }
            case 'questionMultipleChoice': {
                blockImports.push(import('$lib/components/Blocks/MultipleChoiceQuestion'));
                break;
            }
            case 'questionText': {
                blockImports.push(import('$lib/components/Blocks/TextQuestion'));
                break;
            }
            case 'reviews': {
                blockImports.push(import('$lib/components/Blocks/Reviews'));
                break;
            }
            case 'sliderImage': {
                blockImports.push(import('$lib/components/Blocks/ImageSlider'));
                break;
            }
            case 'sliderTestimonial': {
                blockImports.push(import('$lib/components/Blocks/TestimonialSlider'));
                break;
            }
            case 'text': {
                blockImports.push(import('$lib/components/Blocks/Text'));
                break;
            }
            case 'videoQuestion': {
                blockImports.push(import('$lib/components/Blocks/VideoQuestion'));
                break;
            }
            case 'webinar': {
                blockImports.push(import('$lib/components/Blocks/Webinar'));
                break;
            }
            default:
                break;
        }
    });

    return blockImports;
};

export const getFontName = (theme: Theme, fallbackFontName = 'Roboto') => {
    const { typography, fontId } = theme;

    if (fontId) {
        return fontId;
    }

    return typography.includes(':') ? typography.split(':')[0] : fallbackFontName;
};
